import { create } from 'zustand';
import { adminOrganizationManagementApi } from '@api/api-config.ts';
import { AdminOrganizationListResponse } from '@api/generated/portal-be-admin';

export type OrganizationType = 'SUPPLIER' | 'CLIENT';
/** Zdrojová řada organizace — `organizations.source_type`. */
export type OrganizationSource = 'NP' | 'ECIS';

export interface OrganizationFilters {
  q?: string;
  type?: OrganizationType;
  /** true = jen indexované do Elasticu, false = jen neindexované, undefined = bez filtru. */
  indexed?: boolean;
  /** 'ECIS' = jen firmy z eCIS, 'NP' = jen ze starého nákupního portálu, undefined = bez filtru. */
  source?: OrganizationSource;
  /** Smluvní (fakturační) skupina — contract_groups.id, undefined = bez filtru. */
  contractGroupId?: number;
  page: number;
  size: number;
}

interface PaginationInfo {
  page: number;
  size: number;
  total: number;
  totalPages: number;
  hasPrev: boolean;
  hasNext: boolean;
}

interface AdminOrganizationStoreState {
  loading: boolean;
  error: string | null;
  data: AdminOrganizationListResponse[] | null;
  pagination: PaginationInfo | null;
  /** ID organizace, u které právě běží ruční reindex — kvůli disablování tlačítka v řádku. */
  reindexingId: number | null;
  loadOrganizations: (filters: OrganizationFilters) => Promise<void>;
  /** Vrací chybovou hlášku z BE, nebo null při úspěchu — stránka z toho udělá toast. */
  reindexOrganization: (organizationId: number) => Promise<string | null>;
}

export const useAdminOrganizationStore = create<AdminOrganizationStoreState>(set => ({
  loading: false,
  error: null,
  data: null,
  pagination: null,
  reindexingId: null,

  loadOrganizations: async (filters: OrganizationFilters) => {
    try {
      set({ loading: true, error: null });
      const response = await adminOrganizationManagementApi.getAdminOrganizations({
        page: filters.page,
        size: filters.size,
        q: filters.q,
        type: filters.type,
        indexed: filters.indexed,
        source: filters.source,
        contractGroupId: filters.contractGroupId,
      });
      const result = response.data;
      set({
        loading: false,
        error: null,
        data: result.items || [],
        pagination: {
          page: result.page,
          size: result.size,
          total: result.total,
          totalPages: result.totalPages,
          hasPrev: result.hasPrev,
          hasNext: result.hasNext,
        },
      });
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Chyba při načítání organizací',
      });
    }
  },

  // Reindex jen publikuje zprávu do fronty — úspěch znamená "odesláno", ne "už je v Elasticu".
  // Chyba se schválně nevrací do `error`, aby kvůli ní nezmizel seznam.
  reindexOrganization: async (organizationId: number) => {
    set({ reindexingId: organizationId });
    try {
      await adminOrganizationManagementApi.reindexOrganization({ id: organizationId });
      return null;
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      return message || 'Nepodařilo se spustit přeindexování';
    } finally {
      set({ reindexingId: null });
    }
  },
}));
